import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import {
  deserializeFicheDePreps,
  serializeFicheDePreps,
} from '@teetsh/app/src/queries/sequences/serializer';
import { FicheDePrep } from '@teetsh/app/src/types/sequences';
import { SEQUENCES_LIST_KEY } from '@teetsh/app/src/queries/sequences/useSequencesFolders';
import { fdpQueryKeys } from '@teetsh/app/src/queries/fdp/useFicheDePrep';
import { SEQUENCE_KEY } from '@teetsh/app/src/queries/sequences/useSequence';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useLimitReached } from '@teetsh/app/src/components/LimitReached/useLimitReached';
import { useTranslation } from 'react-i18next';

type CreateFicheDePrep = Omit<FicheDePrep, 'bo'> & { bo?: FicheDePrep['bo'] };

function createFicheDePrep(ficheDePrep: CreateFicheDePrep, sequenceId: string) {
  return request(`/sequences/${sequenceId}/fiches-de-prep`, {
    method: 'POST',
    body: serializeFicheDePreps(ficheDePrep as FicheDePrep),
  }).then(fdp => deserializeFicheDePreps(fdp));
}

export function useCreateFicheDePrep() {
  const queryClient = useQueryClient();
  const { notifyError } = useNotification();
  const { t } = useTranslation(['sequence']);
  const { openLimitModal } = useLimitReached();
  const { mutateAsync } = useMutation({
    mutationFn: ({
      sequenceId,
      ficheDePrep,
    }: {
      sequenceId: string;
      ficheDePrep: CreateFicheDePrep;
    }) => createFicheDePrep(ficheDePrep, sequenceId),
    onSuccess: fdp => {
      queryClient.setQueryData(
        fdpQueryKeys.detail(fdp.sequenceId, fdp.id),
        fdp,
      );
      queryClient.invalidateQueries({
        queryKey: [SEQUENCE_KEY, fdp.sequenceId],
      });
      queryClient.invalidateQueries({
        queryKey: [SEQUENCES_LIST_KEY],
      });
    },
    onError: err => {
      if (err?.response?.status === 403) {
        openLimitModal({
          requiredPlan: (err?.response as any)?.data?.requiredPlan,
          limit: (err?.response as any)?.data?.limit,
        });
        return;
      }
      notifyError({
        title: t('sequence:messages.seance.createError'),
      });
    },
  });
  return mutateAsync;
}
